import React, { useState, useEffect } from 'react';
import './Gatekeeper.css';

const Gatekeeper = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });

    // ============================================
    // CONFIGURATION - Change these values as needed
    // ============================================
    // Password is obfuscated (base64 encoded + reversed) - not visible in plain text
    // To change password, encode it: btoa('your-password').split('').reverse().join('')
    // Current: '==AMyQzI5VGa' decodes to 'hey#420'
    const PASSWORD_HASH = (() => {
        const encoded = '==AMyQzI5VGa';
        return atob(encoded.split('').reverse().join(''));
    })();
    
    // Launch date/time - Fixed to 6 PM UTC (doesn't restart on refresh)
    const getLaunchDate = () => {
        const stored = localStorage.getItem('heyfren_launch_time');
        if (stored) {
            return new Date(parseInt(stored));
        }
        // Set launch to 6 PM UTC today (or tomorrow if already past 6 PM UTC)
        const now = new Date();
        const utcNow = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
        const launchTime = new Date(utcNow);
        launchTime.setUTCHours(18, 0, 0, 0); // 6 PM UTC
        
        // If it's already past 6 PM UTC today, set it to 6 PM UTC tomorrow
        if (utcNow.getTime() > launchTime.getTime()) {
            launchTime.setUTCDate(launchTime.getUTCDate() + 1);
        }
        
        // Store the launch time so it doesn't change on refresh
        localStorage.setItem('heyfren_launch_time', launchTime.getTime().toString());
        return launchTime;
    };
    const LAUNCH_DATE = getLaunchDate();
    // ============================================

    // Countdown timer effect
    useEffect(() => {
        const calculateTimeRemaining = () => {
            const now = new Date();
            const difference = LAUNCH_DATE.getTime() - now.getTime();

            if (difference > 0) {
                const hours = Math.floor(difference / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);
                setTimeRemaining({ hours, minutes, seconds });
            } else {
                setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
            }
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [LAUNCH_DATE]);

    useEffect(() => {
        // Check if user is already authenticated
        const authStatus = localStorage.getItem('gatekeeper_auth');
        if (authStatus === 'authenticated') {
            setIsAuthenticated(true);
        }
        setIsLoading(false);

        // Disable right-click and common keyboard shortcuts
        const handleContextMenu = (e) => e.preventDefault();
        const handleKeyDown = (e) => {
            // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
            if (
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
                (e.ctrlKey && e.key === 'U')
            ) {
                e.preventDefault();
                return false;
            }
        };

        if (!isAuthenticated) {
            document.addEventListener('contextmenu', handleContextMenu);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isAuthenticated]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Compare password using obfuscated hash (not plain text in code)
        if (password === PASSWORD_HASH) {
            localStorage.setItem('gatekeeper_auth', 'authenticated');
            setIsAuthenticated(true);
            setPassword('');
        } else {
            setError('Incorrect password. Please try again.');
            setPassword('');
        }
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="gatekeeper-loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    // Show gatekeeper if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="gatekeeper-container">
                <div className="gatekeeper-overlay"></div>
                <div className="gatekeeper-content">
                    <div className="gatekeeper-greeting">
                        <img src="/logo192.png" alt="HeyFren Logo" className="gatekeeper-logo-img" />
                    </div>
                    
                    <div className="gatekeeper-countdown">
                        <p className="gatekeeper-launch-date">Going Live in</p>
                        <div className="countdown-timer">
                            <div className="countdown-item">
                                <span className="countdown-value">{String(timeRemaining.hours).padStart(2, '0')}</span>
                                <span className="countdown-label">Hours</span>
                            </div>
                            <span className="countdown-separator">:</span>
                            <div className="countdown-item">
                                <span className="countdown-value">{String(timeRemaining.minutes).padStart(2, '0')}</span>
                                <span className="countdown-label">Minutes</span>
                            </div>
                            <span className="countdown-separator">:</span>
                            <div className="countdown-item">
                                <span className="countdown-value">{String(timeRemaining.seconds).padStart(2, '0')}</span>
                                <span className="countdown-label">Seconds</span>
                            </div>
                        </div>
                    </div>
                    <p className="gatekeeper-subtitle">
                        We're putting the finishing touches on something amazing.
                    </p>

                    <form onSubmit={handleSubmit} className="gatekeeper-form">
                        <div className="gatekeeper-input-group">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                className="gatekeeper-input"
                                autoFocus
                                autoComplete="off"
                            />
                        </div>
                        
                        {error && (
                            <div className="gatekeeper-error">
                                {error}
                            </div>
                        )}

                        <button type="submit" className="gatekeeper-btn">
                            Enter
                        </button>
                    </form>

                    <div className="gatekeeper-footer">
                        <p>© 2025 HeyFren. All rights reserved.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show the actual app if authenticated
    return children;
};

export default Gatekeeper;

